import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UserEditFormComponent } from '../../features/customer-management/user-edit-form/user-edit-form.component';
import { firstValueFrom } from 'rxjs';
import { QUESTION_TYPE_LABELS } from '../../models/question.model';
import { FormsModule } from '@angular/forms';
import type { QRCodeToDataURLOptions } from 'qrcode';
import { QuestionWizardComponent } from '../../features/customer-management/question-wizard/question-wizard.component';


/**
 * Interface representing a saved form with customer details and questions.
 */
interface SavedForm {
  /** Unique identifier for the form */
  id: string;
  /** Customer details including name, email, and password */
  customerDetails: {
    name: string;
    email: string;
    password: string;
  };
  /** Serialized questions data */
  questions: string;
  /** Timestamp when the form was created */
  createdAt: string;
}

/**
 * Interface representing a user in the system.
 */
interface User {
  /** Timestamp when the user was created */
  CreatedAt: string;
  /** Unique identifier for the user */
  UserID: string;
  /** User's first name */
  FirstName: string;
  /** User's last name */
  LastName: string;
  /** User's email address */
  Email: string;
  /** Optional array of questions associated with the user */
  questions?: any[];
  /** Flag indicating if the user's details are expanded in the UI */
  isExpanded?: boolean;
  /** Optional customer data associated with the user */
  customerData?: SavedForm;

  ClusterID: string;
}

/**
 * Interface representing edited user data.
 */
interface EditedUser {
  /** Unique identifier for the user */
  id: string;
  /** Updated customer details */
  customerDetails: {
    name: string;
    email: string;
  };
  /** Array of updated questions */
  questions: any[];
}

/** Type defining sort direction options */
type SortDirection = 'asc' | 'desc';

/**
 * Component for displaying and managing a list of users in the FeedMe Sales Panel.
 *
 * Features:
 * - Displays a paginated list of users with their details and questions
 * - Supports sorting users by creation date in ascending or descending order
 * - Provides inline editing of user details and questions
 * - Includes loading states and error handling
 * - Implements responsive design with modern UI elements
 * - Supports customizable items per page (5, 10, 20, 50)
 * - Includes animated transitions and loading skeletons
 * - Provides user-friendly date formatting in Italian locale
 *
 * @example
 * ```html
 * <app-users-list></app-users-list>
 * ```
 */
@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [CommonModule, FormsModule, UserEditFormComponent,QuestionWizardComponent],
  template: `
    <div class="min-h-screen py-4 sm:py-6">
      <div class="max-w-7xl mx-auto px-3 sm:px-6 lg:px-0">
        <!-- Header Section -->
        <div class="bg-white shadow-lg rounded-2xl px-6 py-8 mb-8 transition-all duration-300 hover:shadow-xl animate-fade-in" style="animation-delay: 100ms">
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <div class="flex items-center gap-4">
              <div class="p-3 bg-secondary/10 rounded-xl">
                <svg class="w-8 h-8 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Gestione Utenti</h1>
                <p class="mt-2 text-base text-gray-600">Visualizza e gestisci tutti gli utenti registrati</p>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2 px-4 py-2 bg-secondary/5 rounded-xl border border-secondary/10">
                <svg class="w-5 h-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span class="text-sm font-medium text-secondary">{{ users.length }} utenti</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Controls Section -->
        <div class="bg-white p-6 rounded-2xl shadow-lg mb-8 transition-all duration-300 hover:shadow-xl animate-fade-in" style="animation-delay: 200ms">
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
            <!-- Left Controls -->
            <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <!-- Items Per Page -->
              <div class="relative">
                <label for="itemsPerPage" class="block text-sm font-medium text-gray-700 mb-1">
                  Elementi per pagina
                </label>
                <div class="relative">
                  <select
                    id="itemsPerPage"
                    [(ngModel)]="itemsPerPage"
                    (change)="onItemsPerPageChange()"
                    class="appearance-none bg-white border border-gray-300 rounded-lg pl-4 pr-10 py-2.5 text-sm text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-primary/60 transition-colors duration-200"
                  >
                    <option [value]="5">5 elementi</option>
                    <option [value]="10">10 elementi</option>
                    <option [value]="20">20 elementi</option>
                    <option [value]="50">50 elementi</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <svg class="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Sort Controls -->
              <div class="relative">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Ordine ID
                </label>
                <button
                  (click)="toggleSortDirection()"
                  class="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:text-secondary hover:border-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                  [title]="sortDirection === 'asc' ? 'ID crescente' : 'ID decrescente'"
                >
                  @if (sortDirection === 'asc') {
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    <span>Crescente</span>
                  } @else {
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4 4m0 0l4-4m-4 4v-12" />
                    </svg>
                    <span>Decrescente</span>
                  }
                </button>
              </div>
            </div>

            <!-- Right Controls - User Count -->
            <div class="px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span class="text-gray-600">Mostrando</span>
                <span class="font-medium text-primary">{{ startIndex + 1 }}-{{ endIndex }}</span>
                <span class="text-gray-600">di</span>
                <span class="font-medium text-primary">{{ users.length }}</span>
                <span class="text-gray-600">utenti</span>
              </div>
            </div>
          </div>
        </div>

        @if (isLoading) {
          <div class="mt-4 sm:mt-8 animate-fade-in" style="animation-delay: 300ms">
            <div class="animate-pulse space-y-4">
              @for (i of [1, 2]; track i) {
                <div class="bg-white shadow-sm rounded-lg p-6">
                  <div class="flex items-center space-x-4">
                    <div class="flex-1">
                      <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div class="space-y-3 mt-4">
                        <div class="h-3 bg-gray-200 rounded"></div>
                        <div class="h-3 bg-gray-200 rounded w-5/6"></div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        } @else if (error) {
          <div class="mt-4 sm:mt-8 rounded-lg bg-red-50 p-4 animate-fade-in" style="animation-delay: 300ms">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Si è verificato un errore</h3>
                <p class="mt-2 text-sm text-red-700">{{ error }}</p>
                <button
                  (click)="fetchUsers()"
                  class="mt-3 text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  Riprova
                </button>
              </div>
            </div>
          </div>
        } @else if (!users.length) {
          <div class="mt-4 sm:mt-8 text-center rounded-lg bg-white p-6 sm:p-8 animate-fade-in" style="animation-delay: 300ms">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Nessun utente registrato</h3>
            <p class="mt-1 text-sm text-gray-500">Non ci sono utenti registrati nel sistema.</p>
          </div>
        } @else {
          <div class="grid grid-cols-1 gap-4 sm:gap-6">
            @if (selectedUserForQuestions) {
              <app-question-wizard
                [userId]="selectedUserForQuestions"
                (cancel)="selectedUserForQuestions = null"
              />
            } @else {
            @for (user of paginatedUsers; track user.UserID; let i = $index) {
              @if (user.customerData) {
                @if (editingUser && editingUser.id === user.customerData.id) {
                  <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 animate-fade-in" style="animation-delay: {{ 300 + (i * 100) }}ms">
                    <app-user-edit-form
                      [user]="editingUser"
                      (save)="onSaveUser($event)"
                      (cancel)="onCancelEdit()"
                      [inlineMode]="true"
                    />
                  </div>
                } @else {
                  <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 animate-fade-in" style="animation-delay: {{ 300 + (i * 100) }}ms">
                    <div class="md:flex">
                      <!-- Contact Information Section -->
                      <div class="md:w-1/3 p-6 border-b md:border-b-0 md:border-r border-gray-200">
                        <div class="flex flex-col items-center">
                          <!-- User ID Circle -->
                          <div class="w-20 h-20 rounded-full bg-secondary/10 flex items-center justify-center mb-4 animate-fade-in text-xl font-semibold text-secondary" style="animation-delay: {{ 400 + (i * 100) }}ms">
                            {{ user.UserID }}
                          </div>

                          <!-- Registration Date -->
                          <div class="flex items-center gap-2 text-sm text-gray-600 mb-6 animate-fade-in" style="animation-delay: {{ 500 + (i * 100) }}ms">
                            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>Registrato il {{ formatDate(user.CreatedAt) }}</span>
                          </div>

                          <!-- User Details -->
                          <div class="text-center animate-fade-in" style="animation-delay: {{ 600 + (i * 100) }}ms">
                            <h3 class="text-lg font-semibold text-gray-900">{{ user.customerData.customerDetails.name }}</h3>
                            <p class="text-sm text-gray-500">{{ user.customerData.customerDetails.email }}</p>
                            <div class="mt-2 flex items-center justify-center gap-2">
                              <p class="text-sm text-gray-500">app.feed-me.it/survey/{{ user.ClusterID }}</p>
                              <button
                                (click)="copyToClipboard('app.feed-me.it/survey/' + user.ClusterID)"
                                class="text-secondary hover:text-secondary-dark focus:outline-none"
                                title="Copia URL"
                              >
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-12a2 2 0 00-2-2h-2M8 5a2 2 0 002 2h4a2 2 0 002-2M8 5a2 2 0 012-2h4a2 2 0 012 2" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          <!-- Edit Button -->
                          <div class="flex flex-col gap-2 mt-4 animate-fade-in" style="animation-delay: {{ 700 + (i * 100) }}ms">
                            <!-- Edit Button -->
                            <button
                              (click)="onModifyUser(user.customerData)"
                              class="w-full inline-flex items-center justify-center px-4 py-2 border border-secondary text-sm font-medium rounded-md text-secondary hover:bg-secondary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-all duration-200"
                            >
                              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Modifica
                            </button>

                            <!-- Pulsante Aggiungi Domande -->
                            <button
                              (click)="onAddQuestions(user.UserID)"
                              class="w-full inline-flex items-center justify-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-700 hover:bg-green-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 transition-all duration-200"
                            >
                              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                              </svg>
                              Aggiungi Domande
                            </button>

                            <!-- PDF Generation Button -->
                            <button
                              (click)="generateUserPDF(user)"
                              class="w-full inline-flex items-center justify-center px-4 py-2 border border-primary text-sm font-medium rounded-md text-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              [disabled]="generatingPDF[user.UserID]"
                            >
                              @if (generatingPDF[user.UserID]) {
                                <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generazione PDF...
                              } @else {
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                Genera PDF
                              }
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Questionnaire Section -->
                      <div class="md:w-2/3 p-6">
                        <div class="flex items-center justify-between mb-4 animate-fade-in" style="animation-delay: {{ 800 + (i * 100) }}ms">
                          <h4 class="font-medium text-gray-900">Questionario</h4>
                          <span class="text-sm text-gray-500">{{ user.questions?.length || 0 }} domande</span>
                        </div>
                        <div class="space-y-4">
                          @if (user.questions?.length) {
                            @for (question of user.questions; track question.queryId; let qi = $index) {
                              <div class="p-4 bg-gray-50 rounded-lg animate-fade-in" style="animation-delay: {{ 900 + (i * 100) + (qi * 50) }}ms">
                                <div class="flex items-center justify-between mb-2">
                                  <span class="text-xs px-2 py-1 rounded-full"
                                        [class.bg-blue-100.text-blue-700]="question.type === '1'"
                                        [class.bg-green-100.text-green-700]="question.type === '2'"
                                        [class.bg-green-100.text-green-700]="question.type === '3'"
                                        [class.bg-green-100.text-green-700]="question.type === '4'"
                                        [class.bg-purple-100.text-purple-700]="question.type === '21'">
                                    {{ getQuestionTypeLabel(question.type) }}
                                  </span>
                                </div>
                                <p class="text-sm text-gray-700">{{ question.title }}</p>
                                @if (question.type === '21' && question.url) {
                                  <a href="{{ question.url }}" target="_blank" class="mt-2 text-sm text-secondary hover:text-secondary-dark inline-flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    Link
                                  </a>
                                }
                                @if (question.type === '4' && question.responseType) {
                                  <p class="text-sm text-gray-700">{{ question.responseType }}</p>
                                }
                              </div>
                            }
                          } @else {
                            <p class="text-sm text-gray-500 text-center py-4 animate-fade-in" style="animation-delay: {{ 900 + (i * 100) }}ms">Nessuna domanda presente</p>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                }
              }
            }
            }
          </div>

          <!-- Pagination -->
          @if (!selectedUserForQuestions) {
          <div class="mt-6 flex justify-center animate-fade-in" style="animation-delay: {{ 300 + (paginatedUsers.length * 100) }}ms">
            <nav class="flex items-center gap-2">
              <button
                type="button"
                [disabled]="currentPage === 1"
                (click)="goToPage(currentPage - 1)"
                class="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-primary/5 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              @for (page of visiblePages; track page) {
                <button
                  type="button"
                  (click)="goToPage(page)"
                  class="px-4 py-2 rounded-lg border hover:shadow-sm transition-all duration-200 font-medium"
                  [class]="currentPage === page ? 'bg-secondary text-white border-secondary hover:bg-secondary/90' : 'border-gray-300 hover:bg-primary/5 hover:border-primary'"
                >
                  {{ page }}
                </button>
              }

              <button
                type="button"
                [disabled]="currentPage === totalPages"
                (click)="goToPage(currentPage + 1)"
                class="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-primary/5 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </nav>
          </div>
          }
        }
      </div>
    </div>
  `,
  styles: [`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fade-in {
      opacity: 0;
      animation: fadeIn 0.5s ease-out forwards;
    }
  `]
})
export class UsersListComponent implements OnInit {
  private readonly logoPath = 'assets/LOGO.png';

  selectedUserForQuestions: string | null = null;

  /** Array of all users in the system */
  users: User[] = [];

  /** Flag indicating if data is being loaded */
  isLoading = true;

  /** Error message to display if data fetching fails */
  error: string | null = null;

  /** Currently edited user form data */
  editingUser: SavedForm | null = null;

  /** Current page number in pagination */
  currentPage = 1;

  /** Number of items to display per page */
  itemsPerPage = 5;

  /** Total number of pages based on items per page */
  totalPages = 1;

  /** Array of page numbers to display in pagination */
  visiblePages: number[] = [];

  /** Current sort direction for date sorting */
  sortDirection: SortDirection = 'desc';

  /** Map to track PDF generation loading state for each user */
  generatingPDF: { [key: string]: boolean } = {};

  constructor(private http: HttpClient) { }

  /**
   * Initializes the component by fetching users.
   * Called automatically by Angular after the component is created.
   */
  ngOnInit() {
    this.fetchUsers();
  }

  /**
   * Gets the starting index of the current page.
   * Used to calculate which items to display and for showing the current range.
   * @returns The index of the first item on the current page
   */
  get startIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage;
  }

  /**
   * Gets the ending index of the current page.
   * Used to calculate which items to display and for showing the current range.
   * @returns The index of the last item on the current page
   */
  get endIndex(): number {
    return Math.min(this.startIndex + this.itemsPerPage, this.users.length);
  }

  /**
   * Gets the users for the current page after sorting.
   * Applies current sorting and pagination settings.
   * @returns Array of users for the current page
   */
  get paginatedUsers(): User[] {
    const sortedUsers = this.getSortedUsers();
    return sortedUsers.slice(this.startIndex, this.endIndex);
  }

  /**
   * Sorts users based on their ID.
   * Uses the current sort direction to determine order.
   * @returns Sorted array of users
   */
  getSortedUsers(): User[] {
    return [...this.users].sort((a, b) => {
      const idA = parseInt(a.UserID);
      const idB = parseInt(b.UserID);
      return this.sortDirection === 'asc'
        ? idA - idB
        : idB - idA;
    });
  }

  /**
   * Updates pagination based on current users and items per page.
   * Calculates total pages and visible page numbers.
   * Ensures current page is valid and generates the array of visible page numbers.
   */
  updatePagination() {
    this.totalPages = Math.ceil(this.users.length / this.itemsPerPage);

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const maxVisiblePages = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    this.visiblePages = Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  }

  /**
   * Navigates to a specific page in the pagination.
   * Validates the page number and updates pagination if valid.
   * @param page The page number to navigate to
   */
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  /**
   * Handles changes to the items per page setting.
   * Resets to first page and updates pagination.
   * Called when the user changes the number of items to display per page.
   */
  onItemsPerPageChange() {
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Formats a date string to Italian locale format.
   * Converts the date to a user-friendly format with day, month, and year.
   * @param date The date string to format
   * @returns Formatted date string in Italian locale
   */
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }

  /**
   * Gets the label for a question type.
   * Retrieves the Italian label for the given question type from the QUESTION_TYPE_LABELS constant.
   * @param type The question type identifier
   * @returns The label for the question type in Italian
   */
  getQuestionTypeLabel(type: string): string {
    return QUESTION_TYPE_LABELS[type as keyof typeof QUESTION_TYPE_LABELS] || '';
  }

  /**
   * Fetches all users and their associated questions from the API.
   * Updates the users array with formatted data and handles loading states and errors.
   * Automatically called when the component initializes.
   */
  async fetchUsers() {
    this.isLoading = true;
    this.error = null;

    try {
      const users = await firstValueFrom(
        this.http.get<User[]>(`${environment.apiUrl}/Users`)
      );

      const usersWithQuestions = await Promise.all(
        users.map(async (user) => {
          const questions = await this.fetchUserQuestions(user);
          const formattedQuestions = questions.map(q => ({
            queryId: q.QueryID.toString(),
            type: q.TypeID.toString(),
            title: q.Body,
            ...(q.TypeID === 21 && { url: q.Link }),
            ...(q.TypeID === 4 && { responseType: q.responseType })
          }));

          return {
            ...user,
            questions: formattedQuestions,
            customerData: {
              id: user.UserID,
              customerDetails: {
                name: `${user.FirstName}`,
                email: user.Email,
                password: ''
              },
              questions: JSON.stringify(formattedQuestions),
              createdAt: user.CreatedAt
            }
          };
        })
      );

      this.users = usersWithQuestions.sort((a, b) =>
        new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime()
      );

      this.updatePagination();
      this.isLoading = false;
    } catch (error) {
      console.error('Error fetching users:', error);
      this.error = 'Si è verificato un errore durante il recupero degli utenti.';
      this.isLoading = false;
    }
  }

  /**
   * Fetches questions for a specific user.
   * @param user The user to fetch questions for
   * @returns Array of questions for the user
   */
  async fetchUserQuestions(user: User): Promise<any[]> {
    try {
      return await firstValueFrom(
        this.http.get<any[]>(`${environment.apiUrl}/dashboard/AllQuestions`, {
          params: { Email: user.Email }
        })
      );
    } catch (error) {
      console.error('Error fetching questions for user:', user.Email, error);
      return [];
    }
  }

  /**
   * Sets a user for editing.
   * @param user The user form data to edit
   */
  onModifyUser(user: SavedForm) {
    this.editingUser = user;
  }

  /**
   * Saves edited user data and updates questions.
   * @param editedUser The edited user data to save
   */
  async onSaveUser(editedUser: EditedUser) {
    try {
      // Find the user in the list and update local state immediately
      const userIndex = this.users.findIndex(u => u.customerData?.id === editedUser.id);
      if (userIndex !== -1 && this.users[userIndex].customerData) {
        // Update local state first for immediate UI feedback
        this.users[userIndex].customerData = {
          ...this.users[userIndex].customerData!,
          customerDetails: {
            ...this.users[userIndex].customerData!.customerDetails,
            name: editedUser.customerDetails.name,
            email: editedUser.customerDetails.email
          },
          questions: JSON.stringify(editedUser.questions)
        };

        // Clear editing state immediately after updating local state
        this.editingUser = null;
      }

      // Make API calls to update the backend
      for (const question of editedUser.questions) {
        await firstValueFrom(
          this.http.put(`${environment.apiUrl}/dashboard/UpdateQuestion/${question.queryId}`, {
            Body: question.title,
            ...(question.type === '21' && { Link: question.url })
          })
        );
      }
    } catch (error) {
      console.error('Error updating user:', error);
      // If there's an error, refresh the data from the server to ensure consistency
      await this.fetchUsers();
    }
  }

  /**
   * Cancels the current user edit operation.
   */
  onCancelEdit() {
    this.editingUser = null;
  }

  /**
   * Toggles the sort direction between ascending and descending.
   * Updates pagination after changing sort direction.
   */
  toggleSortDirection() {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Copies the given text to clipboard
   * @param text The text to copy
   */
  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      // Optional: Add some visual feedback that the URL was copied
      // You could use a toast notification here if you have one
      console.log('URL copied to clipboard');
    });
  }

  /**
   * Generates a QR code data URL with fallback handling
   * @param url The URL to encode in the QR code
   * @returns The QR code data URL or null if generation fails
   */
  private async generateQRCode(url: string): Promise<string | null> {
    try {
      const QRCodeModule = await import('qrcode');
      // Use the default export for QR code generation with proper types
      return await new Promise<string>((resolve, reject) => {
        QRCodeModule.default.toDataURL(url, {
          width: 300,
          margin: 2,
          color: {
            dark: '#2563eb',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'H'
        } as QRCodeToDataURLOptions, (error: Error | null | undefined, dataUrl: string) => {
          if (error) {
            reject(error);
          } else {
            resolve(dataUrl);
          }
        });
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  }

  /**
   * Adds a fallback "No QR Code" box to the PDF
   * @param doc PDF document
   * @param x X position
   * @param y Y position
   * @param width Width of the box
   * @param height Height of the box
   */
  private addQRCodeFallback(doc: any, x: number, y: number, width: number, height: number) {
    // Add error box
    doc.setFillColor(254, 242, 242); // red-50
    doc.setDrawColor(239, 68, 68); // red-500
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y, width, height, 2, 2, 'FD');

    // Add error icon (simple X)
    doc.setDrawColor(239, 68, 68);
    doc.setLineWidth(0.8);
    const padding = 5;
    doc.line(x + padding, y + padding, x + width - padding, y + height - padding);
    doc.line(x + width - padding, y + padding, x + padding, y + height - padding);

    // Add error text
    doc.setTextColor(185, 28, 28); // red-700
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('QR Code', x + width / 2, y + height / 2 - 5, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.text('Generation Failed', x + width / 2, y + height / 2 + 5, { align: 'center' });
  }

  /**
   * Generates a PDF document for a user with QR code and information
   * @param user The user to generate the PDF for
   */
  async generateUserPDF(user: User) {
    try {
      this.generatingPDF[user.UserID] = true;

      // Generate QR code first
      const surveyUrl = `app.feed-me.it/survey/${user.ClusterID}`;
      const qrCodeDataUrl = await this.generateQRCode(surveyUrl);

      // Import jsPDF
      const { jsPDF } = await import('jspdf');

      // Load logo image
      const logoImage = await this.loadImage(this.logoPath);

      // Create new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Create GState constructor type
      const GState = (doc as any).GState;

      const width = doc.internal.pageSize.width;
      const height = doc.internal.pageSize.height;

      // Brand Colors
      const primaryColor: [number, number, number] = [37, 99, 235]; // blue-600
      const secondaryColor: [number, number, number] = [124, 58, 237]; // purple-600
      const yellowColor: [number, number, number] = [253, 224, 71]; // yellow-300
      const whiteColor: [number, number, number] = [255, 255, 255];
      const grayTextColor: [number, number, number] = [71, 85, 105];
      const darkTextColor: [number, number, number] = [30, 41, 59];

      // Create gradient background effect
      for (let i = 0; i < height; i++) {
        const alpha = 0.02 - (i / height) * 0.015;
        doc.setFillColor(yellowColor[0], yellowColor[1], yellowColor[2]);
        doc.setGState(new GState({ opacity: alpha }));
        doc.rect(0, i, width, 1, 'F');
      }

      // Add decorative header
      doc.setFillColor(yellowColor[0], yellowColor[1], yellowColor[2]);
      doc.setGState(new GState({ opacity: 1 }));
      doc.roundedRect(0, 0, width, 60, 0, 0, 'F');

      // Add logo
      const logoWidth = 40;
      const logoHeight = 40;
      if (logoImage) {
        doc.addImage(logoImage, 'PNG', 20, 10, logoWidth, logoHeight);
      }

      // Add company name with modern styling
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(32);
      doc.setFont('helvetica', 'bold');
      doc.text('FeedMe', 70, 30);

      // Add tagline
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Il feedback facile e veloce', 70, 40);

      // Add date on the right with a modern badge style
      const currentDate = new Date().toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      // Date badge background
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(width - 70, 15, 50, 20, 3, 3, 'F');
      // Date text
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(currentDate, width - 45, 25, { align: 'center' });
      doc.setFontSize(7);
      doc.text('DATA GENERAZIONE', width - 45, 30, { align: 'center' });

      // Add decorative elements
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(20, 70, width - 20, 70);

      // User Profile Section - FULL FIRST PAGE
      const pageHeight = height - 40; // Leave space for footer

      // Profile header with large title
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('Profilo Utente', width / 2, 95, { align: 'center' });

      // Main info card
      const mainCardY = 110;
      const mainCardHeight = 100;
      doc.setFillColor(whiteColor[0], whiteColor[1], whiteColor[2]);
      doc.roundedRect(15, mainCardY, width - 30, mainCardHeight, 3, 3, 'F');
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setLineWidth(0.2);
      doc.roundedRect(15, mainCardY, width - 30, mainCardHeight, 3, 3, 'S');

      // User details with improved layout and larger text
      doc.setFontSize(12);
      const userDetails = [
        { label: 'ID Utente', value: String(user.UserID) },
        { label: 'Nome Completo', value: user.customerData?.customerDetails.name || 'N/A' },
        { label: 'Email', value: user.customerData?.customerDetails.email || 'N/A' },
        { label: 'Data Registrazione', value: this.formatDate(user.CreatedAt) },
        { label: 'Totale Domande', value: `${user.questions?.length || 0} domande configurate` }
      ];

      let detailY = mainCardY + 20;
      userDetails.forEach(detail => {
        // Label with accent color
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
        doc.text(`${detail.label}:`, 30, detailY);

        // Value with dark color
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(darkTextColor[0], darkTextColor[1], darkTextColor[2]);
        doc.text(String(detail.value), 130, detailY);
        detailY += 15;
      });

      // Survey URL Card
      const urlCardY = mainCardY + mainCardHeight + 20;
      const urlCardHeight = 60;
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(15, urlCardY, width - 30, urlCardHeight, 3, 3, 'F');

      // URL Section Title
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('URL del Questionario', 30, urlCardY + 20);

      // URL with click indicator
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.textWithLink(surveyUrl, 30, urlCardY + 40, {
        url: `https://${surveyUrl}`
      });

      // Add click indicator
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.circle(25, urlCardY + 38, 1.5, 'F');

      // Add first page footer
      this.addPageFooter(doc, currentDate, secondaryColor, grayTextColor);

      // SECOND PAGE - QUESTIONS
      doc.addPage();

      // Questions header with counter
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('Questionario', width / 2, 30, { align: 'center' });

      // Questions counter badge
      const counterText = `${user.questions?.length || 0} domande configurate`;
      const badgeWidth = doc.getTextWidth(counterText) + 20;
      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2], 0.1);
      doc.roundedRect(width / 2 - badgeWidth / 2, 40, badgeWidth, 15, 3, 3, 'F');
      doc.setFontSize(12);
      doc.text(counterText, width / 2, 50, { align: 'center' });

      if (user.questions && user.questions.length > 0) {
        let questionY = 70;
        const questionSpacing = 65; // Increased spacing between questions
        const questionHeight = 55;

        user.questions.forEach((question, index) => {
          // Check if we need more space
          if (questionY + questionHeight > pageHeight) {
            doc.addPage();
            questionY = 30;
          }

          // Question container
          doc.setFillColor(249, 250, 251);
          doc.roundedRect(20, questionY, width - 40, questionHeight, 3, 3, 'F');

          // Question number circle
          doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
          doc.circle(35, questionY + 15, 10, 'F');
          doc.setTextColor(whiteColor[0], whiteColor[1], whiteColor[2]);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text((index + 1).toString(), 35, questionY + 17, { align: 'center' });

          // Question type badge
          let badgeColor: [number, number, number];
          let badgeTextColor: [number, number, number];
          let badgeText = this.getQuestionTypeLabel(question.type);

          if (question.type === '1') {
            badgeColor = primaryColor;
            badgeTextColor = whiteColor;
          } else if (question.type === '2') {
            badgeColor = secondaryColor;
            badgeTextColor = whiteColor;
          } else {
            badgeColor = yellowColor;
            badgeTextColor = darkTextColor;
          }

          // Draw badge with more space
          doc.setFillColor(badgeColor[0], badgeColor[1], badgeColor[2]);
          const typeBadgeWidth = doc.getTextWidth(badgeText) + 12;
          doc.roundedRect(55, questionY + 8, typeBadgeWidth, 14, 2, 2, 'F');

          // Badge text
          doc.setTextColor(badgeTextColor[0], badgeTextColor[1], badgeTextColor[2]);
          doc.setFontSize(10);
          doc.text(badgeText, 61, questionY + 17);

          // Question title with better formatting
          doc.setTextColor(darkTextColor[0], darkTextColor[1], darkTextColor[2]);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');

          // Handle long titles with proper wrapping
          const maxWidth = width - 80; // Wider content area
          const titleLines = doc.splitTextToSize(question.title, maxWidth);
          titleLines.forEach((line: string, lineIndex: number) => {
            doc.text(line, 35, questionY + 35 + (lineIndex * 12));
          });

          // Add URL for redirection questions with better styling
          if (question.type === '21' && question.url) {
            doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.setFontSize(9);
            const linkText = question.url;
            doc.textWithLink(linkText, width - 45, questionY + 17, {
              url: question.url,
              align: 'right'
            });
            // URL indicator
            doc.circle(width - 50, questionY + 15, 1, 'F');
          }

          questionY += questionSpacing;
        });
      } else {
        // No questions message
        doc.setTextColor(grayTextColor[0], grayTextColor[1], grayTextColor[2]);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'italic');
        doc.text('Nessuna domanda configurata', width / 2, 100, { align: 'center' });
      }

      // Add second page footer
      this.addPageFooter(doc, currentDate, secondaryColor, grayTextColor);

      // THIRD PAGE - QR CODE
      doc.addPage();

      // QR Code header
      doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('Accesso Rapido', width / 2, 30, { align: 'center' });

      // Subtitle
      doc.setTextColor(grayTextColor[0], grayTextColor[1], grayTextColor[2]);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('Scansiona il codice QR per accedere al questionario', width / 2, 45, { align: 'center' });

      // QR Code container with shadow
      const qrContainerWidth = 180;
      const qrContainerHeight = 220;
      const qrContainerX = (width - qrContainerWidth) / 2;
      const qrContainerY = 60;

      // Container shadow
      doc.setFillColor(0, 0, 0);
      doc.setGState(new GState({ opacity: 0.05 }));
      doc.roundedRect(qrContainerX + 3, qrContainerY + 3, qrContainerWidth, qrContainerHeight, 5, 5, 'F');

      // Container background
      doc.setGState(new GState({ opacity: 1 }));
      doc.setFillColor(whiteColor[0], whiteColor[1], whiteColor[2]);
      doc.roundedRect(qrContainerX, qrContainerY, qrContainerWidth, qrContainerHeight, 5, 5, 'F');
      doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setLineWidth(0.2);
      doc.roundedRect(qrContainerX, qrContainerY, qrContainerWidth, qrContainerHeight, 5, 5, 'S');

      if (qrCodeDataUrl) {
        // QR code with larger size
        const qrSize = 120;
        // QR code shadow
        doc.setFillColor(0, 0, 0);
        doc.setGState(new GState({ opacity: 0.1 }));
        doc.roundedRect(qrContainerX + (qrContainerWidth - qrSize) / 2 + 2,
          qrContainerY + 40 + 2,
          qrSize, qrSize, 2, 2, 'F');
        // QR code
        doc.setGState(new GState({ opacity: 1 }));
        doc.addImage(qrCodeDataUrl, 'PNG',
          qrContainerX + (qrContainerWidth - qrSize) / 2,
          qrContainerY + 40,
          qrSize, qrSize);
      } else {
        this.addQRCodeFallback(doc,
          qrContainerX + (qrContainerWidth - 100) / 2,
          qrContainerY + 50,
          100, 100);
      }

      // // URL section with improved styling
      // doc.setFontSize(12);
      // doc.setFont('helvetica', 'bold');
      // doc.setTextColor(darkTextColor[0], darkTextColor[1], darkTextColor[2]);
      // doc.text('URL del questionario:', width / 2, qrContainerY + 180, { align: 'center' });

      // Clickable URL
      doc.setTextColor(primaryColor[1], primaryColor[1], primaryColor[2]);
      doc.setFont('helvetica', 'normal');
      doc.textWithLink(surveyUrl, width / 2, qrContainerY + 195, {
        url: `https://${surveyUrl}`,
        align: 'center'
      });

      // Instructions
      doc.setTextColor(grayTextColor[0], grayTextColor[1], grayTextColor[2]);
      doc.setFontSize(10);
      const instructions = [
        'Utilizza la fotocamera del tuo dispositivo per scansionare il codice QR',
        'oppure',
        'Clicca sull\'URL sopra per accedere direttamente al questionario'
      ];

      instructions.forEach((line, index) => {
        doc.text(line, width / 2, qrContainerY + 220 + (index * 12), { align: 'center' });
      });

      // Add third page footer
      this.addPageFooter(doc, currentDate, secondaryColor, grayTextColor);

      // Save the PDF
      doc.save(`FeedMe-User-${user.UserID}-Profile.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.generatingPDF[user.UserID] = false;
    }
  }

  /**
   * Adds a consistent footer to each page
   * @param doc PDF document
   * @param currentDate Current date string
   * @param secondaryColor Secondary color array
   * @param grayTextColor Gray text color array
   */
  private addPageFooter(
    doc: any,
    currentDate: string,
    secondaryColor: [number, number, number],
    grayTextColor: [number, number, number]
  ) {
    const width = doc.internal.pageSize.width;
    const height = doc.internal.pageSize.height;
    const footerY = height - 20;

    // Footer background
    doc.setFillColor(249, 250, 251);
    doc.rect(0, footerY - 10, width, 30, 'F');

    // Footer content
    doc.setFontSize(9);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.text('Generato da FeedMe Sales Panel', width / 2, footerY, { align: 'center' });
    doc.setTextColor(grayTextColor[0], grayTextColor[1], grayTextColor[2]);
    doc.text(currentDate, width / 2, footerY + 5, { align: 'center' });
  }

  /**
   * Loads an image and returns it as a base64 string
   * @param url URL of the image to load
   * @returns Promise resolving to the base64 string of the image
   */
  private loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';  // Enable CORS
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = () => reject(new Error('Could not load image'));
      img.src = url;
    });
  }

  onAddQuestions(userId: string) {
    console.log('Aggiungi domande per utente', userId);
    this.selectedUserForQuestions = userId;
    // TODO: qui puoi fare un redirect o aprire una modale
  }
}
